import { useState, useEffect } from 'react';
import { hasLifetimeAccess } from '../../supabase/functions/_shared/lifetime-access';
import { useCurrentDojo } from './useCurrentDojo';
import { supabase } from '@/integrations/supabase/client';

export const useTrialStatus = () => {
  const { dojo, loading: dojoLoading } = useCurrentDojo();
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!dojo || dojoLoading) {
        setLoading(true);
        return;
      }

      try {
        console.log('🔍 Checking trial status for dojo:', dojo.name, 'plan:', dojo.plan, 'trial_start:', dojo.trial_start);

        // Always allow Taiga Dojo
        const isTaigaDojo = hasLifetimeAccess(dojo.id);

        if (isTaigaDojo) {
          console.log('✅ Taiga Dojo detected - full access granted');
          setIsTrialExpired(false);
          setTrialDaysLeft(null);
          setLoading(false);
          return;
        }

        // Check if it's a trial plan
        if (dojo.plan === 'trial') {
          if (dojo.trial_start) {
            const trialStart = new Date(dojo.trial_start);
            const now = new Date();
            const daysSinceStart = Math.floor((now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24));
            const daysLeft = Math.max(0, 14 - daysSinceStart); // 14-day trial

            console.log('📅 Trial calculation:', { daysSinceStart, daysLeft, trialStart: trialStart.toISOString() });

            setTrialDaysLeft(daysLeft);

            if (daysLeft <= 0) {
              console.log('❌ Trial expired');
              setIsTrialExpired(true);
            } else {
              console.log('✅ Trial active with', daysLeft, 'days remaining');
              setIsTrialExpired(false);
            }
          } else {
            // Trial plan but no trial_start date - assume expired
            console.log('⚠️ Trial plan without trial_start date - assuming expired');
            setIsTrialExpired(true);
            setTrialDaysLeft(0);
          }
        } else {
          // Not a trial plan (paid or other)
          console.log('💳 Non-trial plan detected:', dojo.plan);
          setIsTrialExpired(false);
          setTrialDaysLeft(null);
        }
      } catch (error) {
        console.error('Error checking trial status:', error);
        setIsTrialExpired(false);
        setTrialDaysLeft(null);
      } finally {
        setLoading(false);
      }
    };

    checkTrialStatus();
  }, [dojo, dojoLoading]);

  const isTaigaDojo = hasLifetimeAccess(dojo?.id);
  const hasActiveAccess = isTaigaDojo || 
                         (dojo?.plan === 'paid' && dojo?.is_active) || 
                         (dojo?.plan === 'pro' && dojo?.is_active) || 
                         (dojo?.plan === 'trial' && !isTrialExpired);

  console.log('🎯 Trial status summary:', {
    dojo: dojo?.name || 'Unknown',
    plan: dojo?.plan || 'Unknown',
    isTrialExpired: isTrialExpired,
    trialDaysLeft: trialDaysLeft,
    hasActiveAccess: hasActiveAccess,
    isTaigaDojo: dojo?.name === 'Taiga'
  });

  return {
    isTrialExpired,
    trialDaysLeft,
    loading,
    isOnTrial: dojo?.plan === 'trial' && !isTrialExpired,
    isPaidPlan: (dojo?.plan === 'paid' || dojo?.plan === 'pro') && dojo?.is_active,
    isTaigaDojo,
    needsUpgrade: dojo?.plan === 'trial' && isTrialExpired,
    isAccountLocked: dojo?.plan === 'trial' && isTrialExpired && !isTaigaDojo,
    hasActiveAccess,
    planType: dojo?.plan === 'trial' ? 'trial' : 
              dojo?.plan === 'paid' || dojo?.plan === 'pro' ? 'paid' : 'unknown'
  };
};
